"use client";

import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { OrgPhoto } from "@/components/ui/OrgPhoto";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NAVIGATION_LINKS } from "@/config/constants";
import { V2_ENDPOINT } from "@/consts";
import { useOrganizations } from "@/hooks/useOrganizations";
import { authenticatedFetchJson, clearClientSessionCookies } from "@/lib/authenticatedFetch";
import { resolveIpfsUrl } from "@/lib/ipfs";
import { enterOrganizationWorkspace } from "@/lib/orgWorkspaceTransition";
import { showOrgContextToast } from "@/lib/richToasts";
import { ROUTES } from "@/lib/routes";
import { toast } from "@/lib/toast";
import { cn, formatUsername } from "@/lib/utils";
import { useManageAccountModalStore } from "@/stores/manageAccountModalStore";
import { Organization } from "@/types/organization";
import { useAuth, useAuthentication, useProfile } from "@b3dotfun/sdk/global-account/react";
import { getMasqueradeInfo, isEmbeddedMasquerade } from "@/lib/masquerade";
import { IconEyeOutline18 } from "nucleo-ui-outline-18";
import { IconArrowBoxRight, IconBuildings } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { IconBellOutline18, IconCheckOutline18, IconCoinsOutline18, IconEnvelopeOutline18, IconGearOutline18, IconPlusOutline18, IconUserOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { useEffect, useMemo, useState } from "react";
interface OrganizationMembersResponse {
  data: {
    items: Array<{
      userId: string;
    }>;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}
export default function UserOrgSwitcher({
  collapsed
}: {
  collapsed?: boolean;
} = {}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  const {
    logout
  } = useAuthentication(process.env.NEXT_PUBLIC_GLOBAL_ACCOUNTS_PARTNER_ID ?? "");
  const {
    data: profileData
  } = useProfile({
    address: undefined,
    name: undefined,
    b3GlobalId: user?.userId
  });
  const openManageAccount = useManageAccountModalStore(state => state.openManageAccount);
  const {
    organizations,
    selectedOrgId,
    selectOrg,
    loading,
    pendingInvites,
    acceptInvite,
    declineInvite,
    isAccepting,
    isDeclining
  } = useOrganizations();
  const [open, setOpen] = useState(false);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  const [invitesModalOpen, setInvitesModalOpen] = useState(false);
  const [orgMemberCounts, setOrgMemberCounts] = useState<Record<string, number>>({});
  const [avatarFailed, setAvatarFailed] = useState(false);
  const resolvedAvatar = useMemo(() => profileData?.avatar ? resolveIpfsUrl(profileData.avatar) : null, [profileData?.avatar]);
  useEffect(() => {
    setAvatarFailed(false);
  }, [resolvedAvatar]);

  // Auto-open invites modal when there are pending invites (once per session)
  useEffect(() => {
    if (pendingInvites.length > 0) {
      const shownKey = "invitesModalShown";
      const lastShownCount = sessionStorage.getItem(shownKey);

      // Show modal if never shown this session, or if there are new invites
      if (!lastShownCount || parseInt(lastShownCount, 10) < pendingInvites.length) {
        setInvitesModalOpen(true);
        sessionStorage.setItem(shownKey, String(pendingInvites.length));
      }
    }
  }, [pendingInvites.length]);
  useEffect(() => {
    if (!open || organizations.length === 0) return;
    const missingOrganizations = organizations.filter(org => orgMemberCounts[org.id] == null);
    if (missingOrganizations.length === 0) return;
    let cancelled = false;
    const loadMemberCounts = async () => {
      const counts = await Promise.all(missingOrganizations.map(async org_0 => {
        try {
          const response = await authenticatedFetchJson<OrganizationMembersResponse>(`${V2_ENDPOINT}/v1/organizations/${org_0.id}/members?limit=100`, {
            orgId: org_0.id,
            showErrorToast: false
          });
          return [org_0.id, response.data.items.length] as const;
        } catch (error) {
          console.error("@@B3OS: Failed to load organization member count:", org_0.id, error);
          return [org_0.id, 0] as const;
        }
      }));
      if (cancelled) return;
      setOrgMemberCounts(prev => ({
        ...prev,
        ...Object.fromEntries(counts)
      }));
    };
    loadMemberCounts();
    return () => {
      cancelled = true;
    };
  }, [open, organizations, orgMemberCounts]);
  const handleSelectOrg = (org_1: Organization) => {
    if (org_1.id === selectedOrgId) {
      setOpen(false);
      return;
    }
    console.log("@@B3OS: Switched to organization:", org_1.id);
    enterOrganizationWorkspace({
      orgId: org_1.id,
      orgName: org_1.name,
      selectOrg,
      onBeforeNavigate: () => setOpen(false),
      navigate: () => router.push(ROUTES.WORKFLOW),
      invalidateQueries: () => {
        queryClient.invalidateQueries();
      }
    });
  };
  const handleAcceptInvite = async (inviteId: string, orgId: string, orgName: string) => {
    setProcessingInviteId(inviteId);
    try {
      await acceptInvite(inviteId);
      enterOrganizationWorkspace({
        orgId,
        orgName,
        selectOrg,
        onBeforeNavigate: () => setInvitesModalOpen(false),
        navigate: () => router.push(ROUTES.WORKFLOW),
        invalidateQueries: () => {
          queryClient.invalidateQueries();
        },
        toastTitle: "Invite accepted",
        toastDescription: "Switching your workspace now."
      });
    } catch (error_0) {
      console.error("@@B3OS: Failed to accept invite:", error_0);
      toast.error("Failed to accept invite");
    } finally {
      setProcessingInviteId(null);
    }
  };
  const handleDeclineInvite = async (inviteId_0: string, orgName_0: string) => {
    setProcessingInviteId(inviteId_0);
    try {
      await declineInvite(inviteId_0);
      showOrgContextToast({
        type: "info",
        title: "Invite declined",
        orgName: orgName_0,
        description: "That organization has been removed from your pending invites."
      });
    } catch (error_1) {
      console.error("@@B3OS: Failed to decline invite:", error_1);
      toast.error("Failed to decline invite");
    } finally {
      setProcessingInviteId(null);
    }
  };
  const handleSignOut = async () => {
    setOpen(false);
    try {
      await logout();
    } finally {
      clearClientSessionCookies();
      window.location.replace(ROUTES.HOME);
    }
  };
  if (loading) {
    if (collapsed) {
      return <div className="flex w-full items-center justify-center p-1">
          <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex h-8 w-8 animate-pulse items-center justify-center rounded-full">
            <IconBuildings className="text-ds-text-disabled h-[14px] w-[14px]" />
          </div>
        </div>;
    }
    return <div className="group flex w-full items-center gap-3 px-1 py-2.5">
        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex h-10 w-10 items-center justify-center rounded-full">
          <IconUserOutline18 className="text-ds-text-disabled h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 h-4 w-24 animate-pulse rounded"></div>
        </div>
      </div>;
  }
  const masqueradeInfo = isEmbeddedMasquerade() ? getMasqueradeInfo() : null;
  const displayName = masqueradeInfo?.targetEmail || formatUsername(profileData?.name) || formatUsername(user?.username) || "Account";
  const selectedOrg = organizations.find(o => o.id === selectedOrgId);
  if (masqueradeInfo) {
    return <div className={cn("group flex w-full items-center", collapsed ? "justify-center rounded-lg p-1" : "rounded-ds-md gap-2.5 border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 dark:border-amber-400/20 dark:bg-amber-400/5")}>
        <IconEyeOutline18 className={cn("shrink-0 text-amber-600 dark:text-amber-400", collapsed ? "h-4 w-4" : "h-[18px] w-[18px]")} />
        {!collapsed && <p className="body-sm-medium truncate text-amber-700 dark:text-amber-300">Masquerading</p>}
      </div>;
  }
  return <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={() => setOpen(!open)} aria-expanded={open} title={collapsed ? displayName : undefined} className={cn("group flex w-full items-center transition-[background-color,border-color,box-shadow,transform] duration-150 ease-out active:scale-[0.98]", collapsed ? "justify-center rounded-lg p-1 hover:bg-white/60 dark:hover:bg-zinc-800/50" : "rounded-ds-md bg-ds-surface-card shadow-ds-soft border-ds-border-default hover:bg-ds-surface-hover gap-3 border px-3 py-2")}>
          <div className={cn("shadow-ds-soft-pressed border-ds-border-default bg-ds-surface-card relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border", collapsed ? "h-8 w-8" : "h-[35px] w-[35px]")}>
            {resolvedAvatar && !avatarFailed ? <img src={resolvedAvatar} alt="Organization avatar" className="h-full w-full object-cover" onError={() => setAvatarFailed(true)} /> : <IconBuildings className={cn("text-ds-text-disabled dark:text-ds-text-muted", collapsed ? "h-[14px] w-[14px]" : "h-[18px] w-[18px]")} />}
            {pendingInvites.length > 0 && <span className={cn("text-3xs absolute flex items-center justify-center rounded-full bg-red-500 font-bold text-white", collapsed ? "-right-0.5 -top-0.5 h-3.5 w-3.5" : "-right-1 -top-1 h-4 w-4")}>
                {pendingInvites.length}
              </span>}
          </div>
          {!collapsed && <div className="min-w-0 flex-1 text-left">
              <p className="body-sm-medium text-ds-text-secondary truncate dark:text-zinc-200">{displayName}</p>
              {selectedOrg && <p className="micro-label text-ds-text-muted truncate leading-tight">{selectedOrg.name}</p>}
            </div>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="border-ds-border-default bg-ds-surface-card w-[320px] p-0" sideOffset={8}>
        <div className="max-h-[calc(var(--radix-popover-content-available-height)-72px)] overflow-y-auto pt-3">
          {/* User Actions Section */}
          <div className="border-ds-border-default border-b p-2">
            <p className="table-header mb-2 px-3">Account</p>
            <Link href={ROUTES.BILLING} onClick={() => setOpen(false)} className="text-ds-text-secondary dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors">
              <IconCoinsOutline18 className="h-4 w-4 shrink-0" />
              <span>Billing</span>
            </Link>
            <button onClick={() => {
            setOpen(false);
            openManageAccount();
          }} className="text-ds-text-secondary dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors">
              <IconUserOutline18 className="h-4 w-4" />
              <span>Manage Account</span>
            </button>
            <Link href={ROUTES.ACCOUNT_SETTINGS} onClick={() => setOpen(false)} className="text-ds-text-secondary dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors">
              <IconBellOutline18 className="h-4 w-4" />
              <span>Notification Settings</span>
            </Link>
            <button onClick={handleSignOut} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20">
              <IconArrowBoxRight className="h-4 w-4" />
              <span>Sign out</span>
            </button>
          </div>

          {/* Organizations Section */}
          {organizations.length > 0 && <div className="border-ds-border-default border-b p-2">
              <p className="table-header mb-2 mt-2 px-3">Your Organizations</p>
              {organizations.map(org_2 => <button key={org_2.id} onClick={() => handleSelectOrg(org_2)} className="dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors">
                  <OrgPhoto src={org_2.photo} alt={org_2.name} size={32} rounded="md" />
                  <div className="flex-1 truncate">
                    <p className="text-ds-text-primary truncate font-medium">{org_2.name}</p>
                    {orgMemberCounts[org_2.id] != null ? <p className="label-xs">
                        {orgMemberCounts[org_2.id]} member{orgMemberCounts[org_2.id] === 1 ? "" : "s"}
                      </p> : <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 mt-1 h-3 w-16 animate-pulse rounded" />}
                  </div>
                  {org_2.id === selectedOrgId && <IconCheckOutline18 className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />}
                </button>)}
            </div>}

          {/* Pending Invites */}
          {pendingInvites.length > 0 && <div className="border-ds-border-default border-b p-2">
              <button onClick={() => {
            setOpen(false);
            setInvitesModalOpen(true);
          }} className="dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors">
                <div className="bg-ds-neutral-100 dark:bg-ds-neutral-700 relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                  <IconEnvelopeOutline18 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-3xs absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 font-bold text-white">
                    {pendingInvites.length}
                  </span>
                </div>
                <div className="flex-1 truncate">
                  <p className="text-ds-text-primary truncate font-medium">Pending Invites</p>
                  <p className="label-xs">
                    {pendingInvites.length} invitation{pendingInvites.length > 1 ? "s" : ""} waiting
                  </p>
                </div>
              </button>
            </div>}

          {/* Organization Actions */}
          <div className="p-2">
            <Link href={`${ROUTES.ONBOARDING}?new=1`} onClick={() => setOpen(false)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20">
              <IconPlusOutline18 className="h-4 w-4" />
              <span>Create New Organization</span>
            </Link>
            <Link href={ROUTES.ORG_SETTINGS} onClick={() => setOpen(false)} className="text-ds-text-secondary dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors">
              <IconGearOutline18 className="h-4 w-4" />
              <span>Organization Settings</span>
            </Link>
            {NAVIGATION_LINKS.filter(l => l.group === "org").map(link => {
            const LinkIcon = link.icon;
            return <Link key={link.href} href={link.href} onClick={() => setOpen(false)} className="text-ds-text-secondary dark:hover:bg-ds-neutral-700/50 hover:bg-ds-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors">
                  <LinkIcon className="h-4 w-4" />
                  <span>{link.label}</span>
                </Link>;
          })}
          </div>
        </div>
      </PopoverContent>
      {/* Pending Invites Modal */}
      <ResponsiveModal open={invitesModalOpen} onOpenChange={setInvitesModalOpen} title="Pending Invites">
        <div className="space-y-4">
          <div className="mb-6">
            <h3 className="text-ds-text-primary text-lg font-semibold">Pending Invites</h3>
            <p className="text-ds-text-muted text-sm">
              You have {pendingInvites.length} pending organization invitation{pendingInvites.length > 1 ? "s" : ""}
            </p>
          </div>

          {pendingInvites.map(invite => <div key={invite.id} className="bg-ds-surface-secondary rounded-lg p-4">
              <div className="mb-3 flex items-start gap-3">
                <div className="bg-ds-info-surface flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                  <IconBuildings className="text-ds-info-text h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-ds-text-primary font-medium">{invite.organizationName}</p>
                  <p className="text-ds-text-muted text-sm">
                    Role: {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                  </p>
                  <p className="caption-xs">Expires: {new Date(invite.expiresAt).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="mt-10 flex gap-2">
                <Button onClick={() => handleAcceptInvite(invite.id, invite.organizationId, invite.organizationName)} disabled={processingInviteId === invite.id} className="flex-1">
                  <span className="flex items-center gap-2 text-xs">
                    {processingInviteId === invite.id && isAccepting ? <>
                        <PixelLoader size="xs" />
                        Accepting...
                      </> : <>
                        <IconCheckOutline18 className="h-3 w-3" />
                        Accept
                      </>}
                  </span>
                </Button>
                <Button variant="white" onClick={() => handleDeclineInvite(invite.id, invite.organizationName)} disabled={processingInviteId === invite.id}>
                  <span className="flex items-center gap-2 text-xs">
                    {processingInviteId === invite.id && isDeclining ? <>
                        <PixelLoader size="xs" />
                        Declining...
                      </> : <>
                        <IconXmarkOutline18 className="h-3 w-3" />
                        Decline
                      </>}
                  </span>
                </Button>
              </div>
            </div>)}
        </div>
      </ResponsiveModal>
    </Popover>;
}